"use client";

import Image from "next/image";
import AnimatedContent from "../AnimatedContent";
import { useTranslation } from "react-i18next";
import SectionContainer from "@/common/SectionContainer";

interface BusinessStep {
  icon: string;
  title: string;
  description: string;
}

interface BusinessFeatureCard {
  image: string;
  title: string;
  description: string;
}

const BusinessSection = () => {
  const { t } = useTranslation();
  const steps = t("BUSINESS_SECTION_STEPS", { returnObjects: true }) as BusinessStep[];
  const cards = t("BUSINESS_SECTION_FEATURE_CARDS", {
    returnObjects: true,
  }) as BusinessFeatureCard[];

  return (
    <SectionContainer
      id="business-section"
      className="section-shell bg-green-50 text-gray-900"
    >
      <AnimatedContent className="section-inner section-stack">
        <div className="section-head">
          <h2 className="section-title">{t("BUSINESS_SECTION_TITLE")}</h2>
          <p className="caption text-gray-600 section-lead">
            {t("BUSINESS_SECTION_SUB_TITLE")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="relative w-full aspect-[193/99] rounded-2xl overflow-hidden shadow-lg">
            <Image
              src="https://zipshow-static-web.s3.ap-northeast-2.amazonaws.com/homepage/color-to-life-biz/business_main_image.png"
              alt={t("BUSINESS_SECTION_MAIN_IMAGE_ALT")}
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover"
            />
          </div>

          <div className="flex flex-col gap-10">
            {cards.map((card) => (
              <div
                key={card.title}
                className="flex flex-col sm:flex-row items-center gap-6 bg-gray-50 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all"
              >
                <div className="relative w-full sm:w-1/2 aspect-[872/349] rounded-xl overflow-hidden">
                  <Image
                    src={card.image}
                    alt={card.title}
                    fill
                    sizes="(max-width: 640px) 100vw, 25vw"
                    className="object-cover"
                  />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h4 className="text-[clamp(1rem,2vw,1.25rem)] font-semibold text-[#207AE8] mb-2">
                    {card.title}
                  </h4>
                  <p className="text-gray-700 text-[clamp(0.9rem,1.3vw,1rem)] whitespace-pre-wrap">
                    {card.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center mt-10">
          {steps.map((step) => (
            <div
              key={step.title}
              className="flex flex-col items-center gap-4 p-6 rounded-xl bg-gradient-to-b from-white to-gray-50 shadow-sm hover:shadow-md transition-all"
            >
              <div className="relative w-14 h-14">
                <Image
                  src={step.icon}
                  alt={step.title}
                  fill
                  sizes="56px"
                  className="object-contain"
                />
              </div>
              <h4 className="text-[clamp(1rem,1.8vw,1.25rem)] font-semibold text-[#207AE8]">
                {step.title}
              </h4>
              <p className="text-gray-700 text-[clamp(0.9rem,1.3vw,1rem)] max-w-[260px]">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </AnimatedContent>
    </SectionContainer>
  );
};

export default BusinessSection;
